import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';

const GROUPS = [
  { key: 'parents', label: 'Parents & Drivers' },
  { key: 'admins', label: 'Admins' },
  { key: 'staff', label: 'Staff' },
  { key: 'students', label: 'Students' },
];

function schoolLabel(school) {
  if (!school) return '';
  const name = school.school_nickname || school.legal_name || `School ${school.id}`;
  const place = [school.city, school.state].filter(Boolean).join(', ');
  return place ? `${name} (${place})` : name;
}

function firstSchool(result) {
  return result.school || result.schools?.[0] || null;
}

function contactValues(result) {
  return [
    ['Email', result.email],
    ['Username', result.username],
    ['Phone', result.phone],
  ].filter(([, value]) => value);
}

function permissionLabel(value) {
  if (!value) return null;
  return String(value).replace(/_/g, ' ').replace('/', ' / ');
}

export function resultRoute(groupKey, result) {
  if (groupKey === 'students') {
    return result.school_id
      ? ['students', { schoolId: result.school_id, selectStudentId: result.id }]
      : null;
  }

  if (groupKey === 'parents') {
    const school = firstSchool(result);
    if (!school?.id) return null;
    // Their account in the school, the way students and staff already open —
    // not straight into the edit form. Looking someone up is nearly always to
    // read them, and editing is one click from the profile.
    return result.type === 'driver'
      ? ['drivers', { schoolId: school.id, selectDriverId: result.id }]
      : ['parents', { schoolId: school.id, selectParentId: result.id }];
  }

  return result.school_id
    ? ['staff', { schoolId: result.school_id, selectStaffId: result.id }]
    : null;
}

function StudentLinks({ result }) {
  const students = result.students || [];
  if (!students.length) return null;

  return (
    <div className="mas-related">
      {students.slice(0, 3).map((student) => (
        <span key={student.id}>
          {student.name}{student.grade !== undefined && student.grade !== null ? `, Grade ${student.grade}` : ''}
        </span>
      ))}
      {students.length > 3 ? <span>+{students.length - 3} more</span> : null}
    </div>
  );
}

function ContactLinks({ result }) {
  const contacts = result.contacts || [];
  if (!contacts.length) return null;

  return (
    <div className="mas-related">
      {contacts.slice(0, 3).map((contact) => (
        <span key={contact.id}>
          {contact.name}{contact.phone ? `, ${contact.phone}` : ''}
        </span>
      ))}
      {contacts.length > 3 ? <span>+{contacts.length - 3} more</span> : null}
    </div>
  );
}

function ResultRow({ groupKey, result, navigate }) {
  const route = resultRoute(groupKey, result);
  const school = firstSchool(result);
  const fields = contactValues(result);
  const secondary = [
    groupKey === 'students' && result.grade !== undefined && result.grade !== null ? `Grade ${result.grade}` : null,
    groupKey !== 'students' ? permissionLabel(result.permission_level) || result.type : result.status,
    schoolLabel(school),
  ].filter(Boolean);

  const handleOpen = () => {
    if (!route) return;
    navigate(route[0], route[1]);
  };

  return (
    <button
      type="button"
      className={`mas-result ${route ? '' : 'disabled'}`}
      onClick={handleOpen}
      disabled={!route}
    >
      <div className="mas-result-copy">
        <div className="mas-result-title-row">
          <span className="mas-result-name">{result.name || `${groupKey} #${result.id}`}</span>
          <span className="mas-result-id">#{result.id}</span>
        </div>
        <div className="mas-result-meta">{secondary.join(' - ')}</div>
        {fields.length ? (
          <div className="mas-result-fields">
            {fields.map(([label, value]) => (
              <span key={`${label}-${value}`}>
                <strong>{label}</strong> {value}
              </span>
            ))}
          </div>
        ) : null}
        {groupKey === 'parents' ? <StudentLinks result={result} /> : null}
        {groupKey === 'students' ? <ContactLinks result={result} /> : null}
      </div>
      <span className="mas-result-arrow" aria-hidden="true">&gt;</span>
    </button>
  );
}

function ResultGroup({ group, results, navigate }) {
  return (
    <section className="mas-group">
      <div className="mas-group-heading">
        <h3>{group.label}</h3>
        <span>{results.length}</span>
      </div>
      {results.length ? (
        <div className="mas-result-list">
          {results.map((result) => (
            <ResultRow
              key={`${group.key}-${result.id}`}
              groupKey={group.key}
              result={result}
              navigate={navigate}
            />
          ))}
        </div>
      ) : (
        <div className="mas-group-empty">No matches</div>
      )}
    </section>
  );
}

export default function MasterSearchPanel({ token, navigate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const trimmedQuery = query.trim();

  useEffect(() => {
    let cancelled = false;

    if (trimmedQuery.length < 2) {
      setResults(null);
      setLoading(false);
      setError('');
      return undefined;
    }

    setLoading(true);
    setError('');

    const timer = setTimeout(() => {
      apiFetch(`/master-search?q=${encodeURIComponent(trimmedQuery)}&limit=12`, {}, token)
        .then((res) => {
          if (!cancelled) setResults(res.data || null);
        })
        .catch((err) => {
          if (!cancelled) {
            setResults(null);
            setError(err.message || 'Search failed');
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token, trimmedQuery]);

  const total = results?.total || 0;
  const hasQuery = trimmedQuery.length >= 2;
  const groupedResults = useMemo(
    () => GROUPS.map((group) => ({
      ...group,
      results: results?.[group.key] || [],
    })),
    [results]
  );

  return (
    <section className="sa-card mas-panel">
      <div className="mas-header">
        <div>
          <h2>Master Search</h2>
          <p>Parents, admins, staff, and students</p>
        </div>
        {hasQuery && !loading ? <span className="mas-total">{total} results</span> : null}
      </div>

      <label className="mas-search">
        <span aria-hidden="true">Search</span>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search name, email, username, phone, school, or ID"
        />
      </label>

      {error ? <div className="sa-error mas-error">{error}</div> : null}
      {loading ? <div className="mas-state">Searching...</div> : null}
      {!loading && !error && hasQuery && total === 0 ? <div className="mas-state">No matches</div> : null}

      {!loading && !error && hasQuery && total > 0 ? (
        <div className="mas-results">
          {groupedResults.map((group) => (
            <ResultGroup key={group.key} group={group} results={group.results} navigate={navigate} />
          ))}
        </div>
      ) : null}
    </section>
  );
}
